import { For, Show, createResource, createSignal, onCleanup } from 'solid-js';
import { api, Track, fmtDur } from '../api';
import { isOperator } from '../me';
import { ErrorLine, PageHeader, StatusPill, useAction } from '../ui';

type RH = { id: string; name: string; kind: string; licences: { id: string; kind: string; valid_from: string; valid_until: string | null }[] };
type Genre = { slug: string; name: string; customer_facing: boolean };

const tone = (s: string) => (s === 'published' ? 'ok' : s === 'failed' ? 'bad' : s === 'retired' ? 'off' : 'warn') as any;

const PAGE = 50;

const PlayIcon = (props: { pause?: boolean }) => (
  <svg viewBox="0 0 16 16" class="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
    {props.pause ? <><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></>
      : <path d="M4 2.5v11a.75.75 0 0 0 1.13.65l9-5.5a.75.75 0 0 0 0-1.3l-9-5.5A.75.75 0 0 0 4 2.5z" />}
  </svg>
);

/* One <audio> for the whole page: the list's play buttons drive it, the bar at the bottom shows it. */
const [playing, setPlaying] = createSignal<Track | null>(null);
const [paused, setPaused] = createSignal(true);
const [pos, setPos] = createSignal(0);
const [dur, setDur] = createSignal(0);
const [audioErr, setAudioErr] = createSignal('');
let audio: HTMLAudioElement | undefined;

async function play(t: Track) {
  if (!audio) return;
  if (playing()?.id === t.id) { audio.paused ? audio.play() : audio.pause(); return; }
  setAudioErr('');
  setPlaying(t);
  try {
    const { url } = await api<{ url: string }>(`/library/tracks/${t.id}/listen`);
    audio.src = url;
    await audio.play();
  } catch (e: any) { setAudioErr(e?.message ?? String(e)); }
}

function PlayerBar() {
  return (
    <div class={`fixed inset-x-0 bottom-0 z-20 border-t border-night-3 bg-night text-static ${playing() ? '' : 'hidden'}`}
      style={{ 'padding-bottom': 'env(safe-area-inset-bottom, 0px)' }}>
      <audio ref={audio} preload="none" onPlay={() => setPaused(false)} onPause={() => setPaused(true)} onEnded={() => setPaused(true)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)} onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onError={() => setAudioErr('Could not play this track')} />
      <div class="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
        <button class="btn btn-circle btn-sm btn-primary" aria-label={paused() ? 'Play' : 'Pause'} onClick={() => playing() && play(playing()!)}>
          <PlayIcon pause={!paused()} /></button>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm"><b>{playing()?.title}</b> <span class="text-lavender">{playing()?.artist}</span></div>
          <div class="mt-1 flex items-center gap-2">
            <span class="mono w-12 text-xs text-mist">{fmtDur(pos() * 1000)}</span>
            <input type="range" class="range range-primary range-xs flex-1" min="0" max={dur() || 0} step="1" value={pos()} aria-label="Position"
              onInput={(e) => { if (audio) audio.currentTime = Number(e.currentTarget.value); }} />
            <span class="mono w-12 text-xs text-mist">{fmtDur((dur() || 0) * 1000)}</span>
          </div>
          <Show when={audioErr()}><div class="text-xs text-error">{audioErr()}</div></Show>
        </div>
        <button class="btn btn-ghost btn-sm text-static" aria-label="Close player" onClick={() => { audio?.pause(); setPlaying(null); }}>✕</button>
      </div>
    </div>
  );
}

export default function Library() {
  const [status, setStatus] = createSignal(isOperator() ? '' : 'published');
  const [q, setQ] = createSignal('');
  const [page, setPage] = createSignal(0);
  const [tracks, { refetch }] = createResource(() => [status(), q(), page()] as const,
    ([s, qq, pg]) => api<{ items: Track[]; total: number }>(`/library/tracks?limit=${PAGE}&offset=${pg * PAGE}${s ? `&status=${s}` : ''}${qq ? `&q=${encodeURIComponent(qq)}` : ''}`));
  const pages = () => Math.max(1, Math.ceil((tracks()?.total ?? 0) / PAGE));
  onCleanup(() => { audio?.pause(); setPlaying(null); });
  const timer = setInterval(() => { if (tracks()?.items.some((t) => t.status === 'uploaded')) refetch(); }, 4000);
  onCleanup(() => clearInterval(timer));

  return (
    <>
      <PageHeader title="Music library" />
      <Show when={isOperator()}><Upload onDone={refetch} /></Show>
      <div class="mb-3 flex flex-wrap gap-2">
        <input class="input input-sm input-bordered" placeholder="Search title, artist or album" value={q()} onInput={(e) => { setQ(e.currentTarget.value); setPage(0); }} aria-label="Search" />
        <Show when={isOperator()}>
          <select class="select select-sm select-bordered" value={status()} onChange={(e) => { setStatus(e.currentTarget.value); setPage(0); }} aria-label="Status">
            <option value="">All statuses</option>
            <For each={['uploaded', 'in_review', 'published', 'retired', 'failed']}>{(s) => <option value={s}>{s.replace('_', ' ')}</option>}</For>
          </select>
        </Show>
      </div>
      <div class="card-z overflow-x-auto p-0">
        <table class="table">
          <thead><tr><th /><th class="th">Track</th><th class="th">Length</th><th class="th">Genres</th><th class="th">Status</th><th /></tr></thead>
          <tbody>
            <For each={tracks()?.items} fallback={<tr><td colspan="6" class="text-ink-3">No tracks.</td></tr>}>
              {(t) => <Row t={t} onChange={refetch} />}
            </For>
          </tbody>
        </table>
      </div>
      <Pager page={page()} pages={pages()} total={tracks()?.total ?? 0} onPage={setPage} />
      <div class={playing() ? 'h-20' : ''} />
      <PlayerBar />
    </>
  );
}

function Pager(props: { page: number; pages: number; total: number; onPage: (p: number) => void }) {
  return (
    <div class="mt-3 flex flex-wrap items-center gap-3 text-sm">
      <span class="text-ink-3">{props.total} tracks · page {props.page + 1} of {props.pages}</span>
      <div class="join ml-auto">
        <button class="btn join-item btn-sm" disabled={props.page === 0} onClick={() => props.onPage(0)} aria-label="First page">«</button>
        <button class="btn join-item btn-sm" disabled={props.page === 0} onClick={() => props.onPage(props.page - 1)}>Previous</button>
        <button class="btn join-item btn-sm" disabled={props.page >= props.pages - 1} onClick={() => props.onPage(props.page + 1)}>Next</button>
        <button class="btn join-item btn-sm" disabled={props.page >= props.pages - 1} onClick={() => props.onPage(props.pages - 1)} aria-label="Last page">»</button>
      </div>
    </div>
  );
}

function Row(props: { t: Track; onChange: () => void }) {
  const [open, setOpen] = createSignal(false);
  return (
    <>
      <tr class="hover">
        <td class="w-10 pr-0">
          <Show when={props.t.duration_ms > 0 && ['published', 'in_review', 'retired'].includes(props.t.status)}>
            <button class={`btn btn-circle btn-sm ${playing()?.id === props.t.id && !paused() ? 'btn-primary' : 'btn-ghost'}`}
              aria-label={playing()?.id === props.t.id && !paused() ? `Pause ${props.t.title}` : `Play ${props.t.title}`} onClick={() => play(props.t)}>
              <PlayIcon pause={playing()?.id === props.t.id && !paused()} /></button>
          </Show>
        </td>
        <td><div class="font-semibold">{props.t.title}</div><div class="text-sm text-ink-3">{props.t.artist}<Show when={props.t.album}> · {props.t.album}</Show>
          <Show when={props.t.made_in_tape}> · made in Tape</Show></div></td>
        <td class="mono">{props.t.duration_ms ? fmtDur(props.t.duration_ms) : '—'}</td>
        <td class="text-sm text-ink-2">{props.t.genres.map((g) => g.genre).join(' · ') || '—'}</td>
        <td><StatusPill tone={tone(props.t.status)}>{props.t.status.replace('_', ' ')}</StatusPill>
          <Show when={props.t.status_detail}><div class="mt-1 max-w-xs truncate text-xs text-error" title={props.t.status_detail!}>{props.t.status_detail}</div></Show></td>
        <td class="text-right"><Show when={isOperator()}><button class="btn btn-ghost btn-xs" onClick={() => setOpen(!open())}>{open() ? 'Close' : 'Review'}</button></Show></td>
      </tr>
      <Show when={open()}><tr><td colspan="6" class="bg-static"><Review id={props.t.id} onChange={props.onChange} /></td></tr></Show>
    </>
  );
}

function Review(props: { id: string; onChange: () => void }) {
  const [t, { mutate }] = createResource(() => props.id, (id) => api<Track>(`/library/tracks/${id}`));
  const [genres] = createResource(() => api<Genre[]>('/library/genres'));
  const [sel, setSel] = createSignal<string[] | null>(null);
  const a = useAction();
  const chosen = () => sel() ?? t()?.genres.map((g) => g.genre) ?? [];
  const toggle = (s: string) => setSel(chosen().includes(s) ? chosen().filter((x) => x !== s) : [...chosen(), s]);
  const act = (action: string) => a.run(async () => {
    mutate(await api<Track>(`/library/tracks/${props.id}/review`, { method: 'POST',
      json: { action, genres: Object.fromEntries(chosen().map((g) => [g, 1])) } }));
    props.onChange();
  });
  return (
    <div class="flex flex-col gap-3 py-2">
      <Show when={t()?.analysis}>
        <div class="mono flex flex-wrap gap-4 text-ink-2">
          <span>tempo {t()!.analysis!.tempo_bpm ?? '—'} bpm</span><span>key {t()!.analysis!.key ?? '—'}</span>
          <span>{t()!.analysis!.loudness_lufs ?? '—'} LUFS (master)</span><span>energy {t()!.analysis!.energy ?? '—'}</span>
        </div>
      </Show>
      <Show when={t()?.tape}>{(tp) => (
        <details class="text-sm">
          <summary class="cursor-pointer text-ink-2">Made in Tape (seed {tp().seed}): check the lyrics and style
            as well as the sound. Published, it plays on scai.fm's stations only, never in shops.</summary>
          <p class="mt-2 text-ink-2">Style: {tp().style || '—'} · {tp().language === 'nl' ? 'Dutch' : 'English'}</p>
          <pre class="mono mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap text-ink-2">{tp().lyrics}</pre>
        </details>
      )}</Show>
      <div class="flex flex-wrap gap-1.5">
        {/* Customer-facing genres plus any hidden sub-genre the track already carries (e.g. from an import). */}
        <For each={genres()?.filter((g) => g.customer_facing || chosen().includes(g.slug))}>{(g) => (
          <button class={`badge badge-lg cursor-pointer ${chosen().includes(g.slug) ? 'badge-primary' : 'badge-outline'}`} aria-pressed={chosen().includes(g.slug)} onClick={() => toggle(g.slug)}>{g.name}</button>
        )}</For>
      </div>
      <div class="flex gap-2">
        <button class="btn btn-sm btn-primary" disabled={a.busy() || !['in_review', 'retired', 'published'].includes(t()?.status ?? '')} onClick={() => act('publish')}>
          {t()?.status === 'published' ? 'Save tags' : 'Publish'}</button>
        <button class="btn btn-sm btn-secondary" disabled={a.busy()} onClick={() => act('reprocess')}>Reprocess</button>
        <button class="btn btn-sm btn-ghost text-error" disabled={a.busy() || t()?.status === 'retired'} onClick={() => act('retire')}>Retire</button>
      </div>
      <ErrorLine error={a.error} />
    </div>
  );
}

function Upload(props: { onDone: () => void }) {
  const [rhs, { refetch }] = createResource(() => api<RH[]>('/library/rights-holders'));
  const [rh, setRh] = createSignal('');
  const [lic, setLic] = createSignal('');
  const [log, setLog] = createSignal<string[]>([]);
  const a = useAction();
  const n = useAction();
  const rhId = () => rh() || rhs()?.[0]?.id || '';
  const licences = () => rhs()?.find((r) => r.id === rhId())?.licences ?? [];
  const licId = () => (licences().some((l) => l.id === lic()) ? lic() : licences()[0]?.id || '');

  const upload = (e: Event) => {
    e.preventDefault();
    const files = (e.currentTarget as HTMLFormElement).querySelector<HTMLInputElement>('input[type=file]')!.files!;
    a.run(async () => {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        const base = f.name.replace(/\.[^.]+$/, '').replace(/^\d+[-_. ]+/, '');
        const [artist, title] = base.includes(' - ') ? base.split(' - ', 2) : ['Unknown artist', base];
        fd.set('title', title.replace(/_/g, ' ')); fd.set('artist', artist.replace(/_/g, ' '));
        fd.set('rights_holder_id', rhId()); fd.set('licence_id', licId()); fd.set('file', f);
        try { await api('/library/tracks', { method: 'POST', body: fd }); setLog([...log(), `queued ${f.name}`]); }
        catch (err: any) { setLog([...log(), `${f.name}: ${err.message}`]); }
      }
      props.onDone();
    });
  };

  const newRights = (e: Event) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    n.run(async () => {
      const r = await api<{ id: string }>('/library/rights-holders', { method: 'POST', json: { name: fd.get('name'), kind: fd.get('kind') } });
      const lf = new FormData();
      lf.set('rights_holder_id', r.id); lf.set('kind', String(fd.get('lkind'))); lf.set('valid_from', String(fd.get('valid_from')));
      lf.set('document', fd.get('document') as File);
      await api('/library/licences', { method: 'POST', body: lf });
      form.reset(); await refetch(); setRh(r.id);
    });
  };

  return (
    <div class="mb-6 grid gap-6 lg:grid-cols-2">
      <form class="card-z flex flex-col gap-3" onSubmit={upload}>
        <h2 class="wordmark text-xl">Upload masters</h2>
        <p class="text-sm text-ink-3">Every track needs a rights holder and a licence. File names like <span class="mono">Artist - Title.flac</span> fill in the tags.</p>
        <div class="flex flex-wrap gap-2">
          <select class="select select-sm select-bordered" value={rhId()} onChange={(e) => setRh(e.currentTarget.value)} aria-label="Rights holder" required>
            <For each={rhs()}>{(r) => <option value={r.id}>{r.name}</option>}</For>
          </select>
          <select class="select select-sm select-bordered" value={licId()} onChange={(e) => setLic(e.currentTarget.value)} aria-label="Licence" required>
            <For each={licences()}>{(l) => <option value={l.id}>{l.kind} from {l.valid_from}</option>}</For>
          </select>
        </div>
        <input type="file" multiple accept="audio/*,.flac,.wav,.aif,.aiff" class="file-input file-input-sm file-input-bordered" required aria-label="Audio files" />
        <button class="btn btn-sm btn-primary self-start" disabled={a.busy() || !licId()}>{a.busy() ? 'Uploading…' : 'Upload'}</button>
        <ErrorLine error={a.error} />
        <Show when={log().length}><ul class="mono max-h-32 overflow-y-auto text-ink-3"><For each={log()}>{(l) => <li>{l}</li>}</For></ul></Show>
      </form>
      <form class="card-z flex flex-col gap-3" onSubmit={newRights}>
        <h2 class="wordmark text-xl">New rights holder</h2>
        <div class="flex flex-wrap gap-2">
          <input name="name" class="input input-sm input-bordered" placeholder="Name" required aria-label="Name" />
          <select name="kind" class="select select-sm select-bordered" aria-label="Kind">
            <For each={['artist', 'label', 'catalogue', 'ai_generated', 'other']}>{(k) => <option value={k}>{k.replace('_', ' ')}</option>}</For>
          </select>
        </div>
        <div class="flex flex-wrap gap-2">
          <select name="lkind" class="select select-sm select-bordered" aria-label="Licence type">
            <For each={['buyout', 'flat_rate', 'per_play', 'other']}>{(k) => <option value={k}>{k.replace('_', ' ')}</option>}</For>
          </select>
          <input name="valid_from" type="date" class="input input-sm input-bordered" required aria-label="Valid from" />
        </div>
        <input name="document" type="file" class="file-input file-input-sm file-input-bordered" required aria-label="Licence document" />
        <button class="btn btn-sm btn-secondary self-start" disabled={n.busy()}>Add with licence</button>
        <ErrorLine error={n.error} />
      </form>
    </div>
  );
}
